import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';

const ICONS: Record<string, string> = {
  retention: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16m.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7z',
  overview: 'M3 13h8V3H3zm0 8h8v-6H3zm10 0h8V11h-8zm0-18v6h8V3z',
  messages: 'M4 4h16v12H5.2L4 17.2zm0-2a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z',
  users: 'M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3m-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3m0 2c-2.3 0-7 1.2-7 3.5V19h14v-2.5C15 14.2 10.3 13 8 13m8 0c-.3 0-.6 0-1 .1 1.2.8 2 2 2 3.4V19h6v-2.5c0-2.3-4.7-3.5-7-3.5',
  rules: 'M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5zm-1 14-4-4 1.4-1.4 2.6 2.6 5.6-5.6L18 9z',
  policies: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9zM8 13h8v2H8zm0 4h8v2H8z',
  webhook: 'M10 15h5.9a3 3 0 1 1 0 2H12a5 5 0 1 1-6.5-4.8v2.1A3 3 0 1 0 10 17zm2.5-11a3 3 0 0 1 1.6 5.6l2.6 4.6-1.7 1-3.6-6.2.9-.5a1 1 0 1 0-1.4-.4l-1.7 1A3 3 0 0 1 12.5 4M6.6 9.3l1.7 1L5.8 15a1 1 0 1 0 1.2 1.3H9a3 3 0 1 1-4.9-2.3z',
  team: 'M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4m0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4',
  keys: 'M7 14a2 2 0 1 1 2-2 2 2 0 0 1-2 2m5.6-4A6 6 0 1 0 12.6 14H17v4h4v-4h2v-4z',
};

function Icon({ name }: { name: string }) {
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden>
      <path d={ICONS[name]} fill="currentColor" />
    </svg>
  );
}

const NAV: { section?: string; to: string; label: string; icon: string; end?: boolean }[] = [
  { to: '/', label: 'Overview', icon: 'overview', end: true },
  { to: '/messages', label: 'Messages', icon: 'messages' },
  { to: '/end-users', label: 'End users', icon: 'users' },
  { section: 'Configuration', to: '/settings/moderation', label: 'Moderation rules', icon: 'rules' },
  { to: '/settings/policies', label: 'Terms & policies', icon: 'policies' },
  { to: '/settings/webhook', label: 'Webhook', icon: 'webhook' },
  { to: '/settings/retention', label: 'Data retention', icon: 'retention' },
  { section: 'Organization', to: '/team', label: 'Team', icon: 'team' },
  { to: '/keys', label: 'API keys', icon: 'keys' },
];

export function Layout() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [loc.pathname]);

  if (!me) return null;
  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className={`shell ${open ? 'nav-open' : ''}`}>
      <aside className="sidebar">
        <div className="brand">
          <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden>
            <path d="M16 2 4 7v8c0 7.5 5.1 13.4 12 15 6.9-1.6 12-7.5 12-15V7z" fill="var(--accent)" />
            <path d="m11 16 3.5 3.5L21.5 12" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>MsgGuard</span>
        </div>
        <nav>
          {NAV.map((item) => (
            <div key={item.to}>
              {item.section && <div className="nav-section">{item.section}</div>}
              <NavLink to={item.to} end={item.end} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            </div>
          ))}
        </nav>
      </aside>
      <div className="scrim" onClick={() => setOpen(false)} />
      <div className="main">
        <header className="topbar">
          <button className="btn btn-ghost menu-btn" onClick={() => setOpen((o) => !o)} aria-label="Toggle navigation">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
              <path d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" fill="currentColor" />
            </svg>
          </button>
          <div className="org-name" title={me.organization.name}>
            {me.organization.name}
          </div>
          <div className="topbar-user">
            <div className="user-meta">
              <span className="user-email">{me.user.email}</span>
              <span className={`role-pill role-${me.user.role}`}>{me.user.role}</span>
            </div>
            <button className="btn btn-sm" onClick={onLogout}>
              Log out
            </button>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
